import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { DateTime } from 'luxon';

import { WindowService } from 'src/shared/providers/window.service';
import { SharedModule } from 'src/shared/shared.module';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { MonthlyFinanceQueriesService } from 'src/shared/services/monthly-finance-queries.service';
import {
  MONTH_ATTRIBUTION_QUERY_PARAM,
  MonthAttributionService,
  monthAttributionQueryValue,
  parseMonthAttribution,
} from 'src/shared/services/month-attribution.service';
import { OnboardingDummyDataService } from 'src/shared/services/onboarding-dummy-data.service';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { DemoDataService } from 'src/shared/services/demo-data.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import {
  MonthAttribution,
  MonthlyFinanceCategory,
  MonthlyFinanceItem,
  MonthlyFinanceKind,
  MonthlyFinanceMonthValue,
  MonthlyFinanceProfitAndLoss,
  MonthlyFinanceSubcategory,
} from 'src/models/monthly-finance.model';
import { FinanceHistoryDialogComponent } from './finance-history-dialog/finance-history-dialog.component';
import { UploadProjectionsModalComponent } from './upload-projections-modal/upload-projections-modal.component';
import { MonthAttributionSwitchComponent } from 'src/shared/components/month-attribution-switch/month-attribution-switch.component';
import { isPdfImportSupported } from 'src/shared/util/income-estimate-pdf-parser';

type Layer = MonthlyFinanceKind;

/** A month column: its header and whether Actuals can exist there yet. */
interface MonthVM {
  label: string;
  isNow: boolean;
  disabled: boolean;
}

/** A read-only month cell of a group row or a total row. */
interface MonthCellVM {
  value: string;
  disabled: boolean;
  isNow: boolean;
}

/** An editable month cell of a line item. */
interface CellVM {
  disabled: boolean;
  isNow: boolean;
  manual: boolean;
  /** What the input shows in a single-property view. */
  display: string;
  /** What the read-only cell shows across all properties. */
  aggregate: string;
}

interface RowVM extends PLRow {
  cells: CellVM[];
  total: string;
}

interface GroupVM extends PLGroup {
  expanded: boolean;
  lines: RowVM[];
  cells: MonthCellVM[];
  total: string;
}

/**
 * Everything the grid paints, computed once per data or edit change. The template
 * reads these fields only, so the change detection that every hover, keystroke and
 * scroll triggers costs nothing: the cell math no longer reruns per cell per cycle.
 */
interface GridVM {
  viewingAll: boolean;
  /** Only Actuals have transactions behind them, so only those numbers open anything. */
  opensTransactions: boolean;
  income: GroupVM[];
  expenses: GroupVM[];
  incomeTotals: MonthCellVM[];
  incomeYear: string;
  expenseTotals: MonthCellVM[];
  expensesYear: string;
  noi: MonthCellVM[];
  noiYear: string;
}

type GroupKind = 'revenue' | 'contra-revenue' | 'expense';

export interface PLRow {
  /** Backend `monthly_finance_type.key` — used as the PUT identifier. */
  key: string;
  label: string;
  /** Sign applied to entered amounts before sending (1 for revenue, -1 for expense/contra). */
  sign: 1 | -1;
}

export interface PLGroup {
  /** Composite key for tracking/expansion: `${categoryKey}:${subcategoryKey}`. */
  id: string;
  label: string;
  kind: GroupKind;
  rows: PLRow[];
}

@Component({
  selector: 'monthly-content',
  templateUrl: './monthly-content.component.html',
  styleUrls: ['./monthly-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, TidyUiComponentsModule, SharedModule, MonthAttributionSwitchComponent],
})
export class MonthlyContentComponent implements OnInit {
  readonly windowService = inject(WindowService);
  readonly addressesQueries = inject(AddressesQueriesService);
  readonly monthlyFinance = inject(MonthlyFinanceQueriesService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly onboardingDummyData = inject(OnboardingDummyDataService);
  private readonly aboutThisPageService = inject(AboutThisPageService);
  private readonly demoDataService = inject(DemoDataService);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly monthAttribution = inject(MonthAttributionService);

  /**
   * First-load gate. While `false`, the grid area renders the skeleton
   * mirror of the table (the only loading indication — the desktop header
   * and mobile toolbar render instantly). Never flips back to `false`, so
   * the skeleton can't replay on route reuse or filter changes.
   */
  isLoaded = false;

  /** Tiny spinner in the desktop-header right slot during background refetches. */
  showRefreshSpinner = false;

  /** Mirrors the bills-content / schedule-page convention. */
  routeReused = false;

  /** True while the about-this-page overlay is up. Renders mock data behind it. */
  readonly showAboutThisPage = signal<boolean>(false);
  /** Cached mock — regenerated when `year()` changes via a computed below. */
  private readonly mockPL = computed<MonthlyFinanceProfitAndLoss>(
    () => this.onboardingDummyData.getMonthlyPLMockData(this.year()),
  );
  // Demo sales account shows the year-aware generated P&L without the overlay.
  private readonly isDemo = this.demoDataService.isDemoAccount();

  /** Returns mock data while overlay is up / demo account, real BE data otherwise. */
  readonly effectivePL = computed<MonthlyFinanceProfitAndLoss | null>(
    () => this.showAboutThisPage() || this.isDemo ? this.mockPL() : this.monthlyFinance.profitAndLoss(),
  );

  dismissAboutThisPage(): void {
    this.aboutThisPageService.dismissAboutThisPage('monthly-pl');
    this.showAboutThisPage.set(false);
  }

  readonly monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  readonly minYear = 2014;

  /** Hides the PDF-import button on browsers without Compression Streams (Safari/iOS < 16.4). */
  readonly canImportProjections = isPdfImportSupported();

  private readonly today = new Date();
  private readonly currentYear = this.today.getFullYear();
  private readonly currentMonth = this.today.getMonth();

  readonly year = signal(this.currentYear);
  readonly layer = signal<Layer>('actual');
  /** Accrual or cash: shared with the Overview and with a month opened in Transactions. */
  readonly attribution = this.monthAttribution.value;
  /** A projection has no payment day, so both readings show the same projected grid. */
  readonly attributionDisabledReason = computed(() =>
    this.layer() === 'projected' ? 'Projections are the same on an accrual or a cash basis.' : null,
  );

  /** Property selector value: 'all' or stringified address id. */
  readonly selectedProperty = signal<string>('all');
  readonly isViewingAll = computed(() => this.selectedProperty() === 'all');
  readonly selectedAddressId = computed<number | null>(() =>
    this.isViewingAll() ? null : Number(this.selectedProperty()),
  );
  readonly currentPropertyLabel = computed(() => {
    if (this.isViewingAll()) {return 'All Properties';}
    const id = this.selectedAddressId();
    const addr = id === null ? undefined : this.addressesQueries.addresses().find(a => a.id === id);
    return addr?.['address_name'] || addr?.address1 || 'Property';
  });
  readonly properties = computed(() => {
    const items: { id: string; label: string }[] = [{ id: 'all', label: 'All Properties' }];
    for (const a of this.addressesQueries.addresses() ?? []) {
      items.push({ id: String(a.id), label: a['address_name'] || a.address1 });
    }
    return items;
  });
  /** {value, viewValue} shape for tidy-header-select. */
  readonly propertyHeaderItems = computed(() =>
    this.properties().map(p => ({ value: p.id, viewValue: p.label })),
  );
  setProperty(id: string) {
    this.selectedProperty.set(id);
    this.pushQueryParams();
  }

  /** Optimistic pending edits keyed `${itemKey}:${year}:${month}` → cents (or null for clear). */
  private readonly pending = signal<Record<string, number | null>>({});

  /** Set of group ids that are currently expanded. Default: all collapsed. */
  private readonly expandedGroups = signal<Set<string>>(new Set());

  async ngOnInit(): Promise<void> {
    this.restoreAttribution();
    // Seed localStorage from metadata (no-op if already populated) before
    // checking, so guest / fresh accounts still get the overlay.
    await this.aboutThisPageService.fetchAndStoreAboutThisPageData();
    this.showAboutThisPage.set(this.aboutThisPageService.checkIfShouldShowAboutThisPage('monthly-pl'));
    await this.loadPage();
  }

  /**
   * First-load + reattach orchestrator. On first call, awaits the P&L
   * query, flips `isLoaded = true`, and arms a `setStoredRoute('/finance')`
   * listener. The listener resolves whenever the user navigates back to
   * the cached finance page — at which point we kick a background refetch
   * with the small header spinner instead of the big bar.
   */
  async loadPage(showSpinner = false, setStoredRoute = true): Promise<void> {
    if (showSpinner) {
      this.showRefreshSpinner = true;
      this.cdr.markForCheck();
    }

    this.pushQueryParams();
    await this.monthlyFinance.profitAndLossQuery.refetch();

    this.isLoaded = true;
    this.showRefreshSpinner = false;
    this.cdr.markForCheck();

    if (this.windowService.isDesktopRes && setStoredRoute) {
      this.storage.setStoredRoute('/finance').then(async () => {
        this.routeReused = true;
        await this.loadPage(true, true);
      });
    }
  }

  /**
   * A shared link or a refresh carries the reading in the URL. Without one, the page keeps
   * the reading picked elsewhere this session and writes it back, so the URL always says.
   */
  private restoreAttribution(): void {
    const fromUrl = parseMonthAttribution(this.route.snapshot.queryParams[MONTH_ATTRIBUTION_QUERY_PARAM]);
    if (fromUrl) {
      this.attribution.set(fromUrl);
    } else {
      this.writeAttributionToUrl();
    }
  }

  setAttribution(value: MonthAttribution): void {
    this.attribution.set(value);
    this.writeAttributionToUrl();
  }

  private writeAttributionToUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [MONTH_ATTRIBUTION_QUERY_PARAM]: monthAttributionQueryValue(this.attribution()) },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private pushQueryParams(): void {
    this.monthlyFinance.addressId.set(this.selectedAddressId());
    this.monthlyFinance.year.set(this.year());
    this.monthlyFinance.kind.set(this.layer());
  }

  // -- Group derivation from BE response -------------------------------------

  private extractGroups(categoryKey: 'income' | 'operating_expenses'): PLGroup[] {
    const cats = this.effectivePL()?.categories ?? [];
    const cat = cats.find(c => c.key === categoryKey);
    if (!cat) {return [];}
    return cat.subcategories.map(sub => this.toGroup(cat, sub));
  }

  private toGroup(cat: MonthlyFinanceCategory, sub: MonthlyFinanceSubcategory): PLGroup {
    const kind: GroupKind = cat.key === 'operating_expenses'
      ? 'expense'
      : sub.key === 'contra_revenue' ? 'contra-revenue' : 'revenue';
    return {
      id: `${cat.key}:${sub.key}`,
      label: sub.label,
      kind,
      rows: sub.items.map(it => ({ key: it.key, label: it.label, sign: kind === 'revenue' ? 1 : -1 })),
    };
  }

  readonly incomeGroups = computed<PLGroup[]>(() => this.extractGroups('income'));
  readonly expenseGroups = computed<PLGroup[]>(() => this.extractGroups('operating_expenses'));

  // -- View model --------------------------------------------------------------

  readonly months = computed<MonthVM[]>(() =>
    this.monthsShort.map((label, i) => ({ label, isNow: this.isCurrentMonth(i), disabled: this.isDisabledCell(i) })),
  );

  readonly grid = computed<GridVM>(() => {
    const months = this.months();
    const monthCells = (value: (i: number) => number | null): MonthCellVM[] =>
      months.map((m, i) => ({ value: this.formatOrDash(value(i)), disabled: m.disabled, isNow: m.isNow }));
    return {
      viewingAll: this.isViewingAll(),
      opensTransactions: this.layer() === 'actual' && !this.showAboutThisPage() && !this.isDemo,
      income: this.incomeGroups().map(group => this.buildGroup(group, months)),
      expenses: this.expenseGroups().map(group => this.buildGroup(group, months)),
      incomeTotals: monthCells(i => this.totalIncomeMonth(i)),
      incomeYear: this.formatCompact(this.totalIncomeYear()),
      expenseTotals: monthCells(i => this.totalExpensesMonth(i)),
      expensesYear: this.formatCompact(this.totalExpensesYear()),
      noi: monthCells(i => this.noiMonth(i)),
      noiYear: this.formatCompact(this.noiYear()),
    };
  });

  private buildGroup(group: PLGroup, months: MonthVM[]): GroupVM {
    const expanded = this.isExpanded(group.id);
    return {
      ...group,
      expanded,
      lines: expanded ? group.rows.map(row => this.buildRow(row, months)) : [],
      cells: months.map((m, i) => ({ value: this.formatOrDash(this.groupMonth(group, i)), disabled: m.disabled, isNow: m.isNow })),
      total: this.formatCompact(this.groupYear(group)),
    };
  }

  private buildRow(row: PLRow, months: MonthVM[]): RowVM {
    const year = this.rowYear(row.key);
    return {
      ...row,
      cells: months.map((m, i) => ({
        disabled: m.disabled,
        isNow: m.isNow,
        manual: !m.disabled && this.isManual(row.key, i),
        display: m.disabled ? '' : this.displayValue(row.key, i),
        aggregate: m.disabled ? '' : this.formatOrDash(this.cellValue(row.key, i)),
      })),
      total: year === 0 ? '—' : this.formatCompact(year),
    };
  }

  /** Flat lookup of items by key for fast cell reads. */
  private readonly itemsByKey = computed<Record<string, MonthlyFinanceItem>>(() => {
    const map: Record<string, MonthlyFinanceItem> = {};
    for (const cat of this.effectivePL()?.categories ?? []) {
      for (const sub of cat.subcategories) {
        for (const item of sub.items) {map[item.key] = item;}
      }
    }
    return map;
  });

  // -- Layer / year navigation -----------------------------------------------

  setLayer(l: Layer) {
    this.layer.set(l);
    this.pushQueryParams();
  }

  isCurrentMonth(monthIndex: number): boolean {
    return this.year() === this.currentYear && monthIndex === this.currentMonth;
  }

  canHaveActual(monthIndex: number): boolean {
    const y = this.year();
    if (y < this.currentYear) {return true;}
    if (y > this.currentYear) {return false;}
    return monthIndex <= this.currentMonth;
  }

  isDisabledCell(monthIndex: number): boolean {
    return this.layer() === 'actual' && !this.canHaveActual(monthIndex);
  }

  readonly canGoBack = computed(() => this.year() > this.minYear);

  prevYear() {
    if (this.year() > this.minYear) {
      this.year.update(y => y - 1);
      this.pushQueryParams();
    }
  }
  nextYear() {
    this.year.update(y => y + 1);
    this.pushQueryParams();
  }

  // -- Collapse state --------------------------------------------------------

  isExpanded(groupId: string): boolean {
    return this.expandedGroups().has(groupId);
  }

  toggleGroup(groupId: string) {
    this.expandedGroups.update(set => {
      const next = new Set(set);
      if (next.has(groupId)) {next.delete(groupId);} else {next.add(groupId);}
      return next;
    });
  }

  expandAll() {
    const all = [...this.incomeGroups(), ...this.expenseGroups()].map(g => g.id);
    this.expandedGroups.set(new Set(all));
  }
  collapseAll() { this.expandedGroups.set(new Set()); }

  // -- Cell read / write -----------------------------------------------------

  /** BE-returned value object for a cell, or null when the item/month is missing. */
  private monthValue(itemKey: string, monthIndex: number): MonthlyFinanceMonthValue | null {
    const item = this.itemsByKey()[itemKey];
    if (!item) {return null;}
    return item.values.find(v => v.year === this.year() && v.month === monthIndex + 1) ?? null;
  }

  private pendingKey(itemKey: string, monthIndex: number): string {
    return `${itemKey}:${this.year()}:${monthIndex + 1}`;
  }

  /** Signed cents currently in effect for this cell — pending edit wins, then BE effective_amount. */
  private effectiveCents(itemKey: string, monthIndex: number): number | null {
    const pk = this.pendingKey(itemKey, monthIndex);
    const pendingMap = this.pending();
    if (Object.prototype.hasOwnProperty.call(pendingMap, pk)) {return pendingMap[pk];}
    return this.monthValue(itemKey, monthIndex)?.effective_amount ?? null;
  }

  /** True iff there's an active override (pending OR overridden_amount). */
  isManual(itemKey: string, monthIndex: number): boolean {
    if (this.isViewingAll()) {return false;}
    const pk = this.pendingKey(itemKey, monthIndex);
    if (Object.prototype.hasOwnProperty.call(this.pending(), pk)) {return this.pending()[pk] !== null;}
    return this.monthValue(itemKey, monthIndex)?.overridden_amount !== null;
  }

  /** Display dollar value (always non-negative — sign is implied by category). */
  cellValue(itemKey: string, monthIndex: number): number | null {
    if (this.isDisabledCell(monthIndex)) {return null;}
    const cents = this.effectiveCents(itemKey, monthIndex);
    return cents === null ? null : Math.abs(cents) / 100;
  }

  displayValue(itemKey: string, monthIndex: number): string {
    const v = this.cellValue(itemKey, monthIndex);
    return v === null ? '' : String(v);
  }

  onCellInput(itemKey: string, monthIndex: number, sign: 1 | -1, raw: string) {
    // The shared demo account is read-only — editing a P&L cell would write to it.
    if (this.demoBlock.block()) {return;}
    if (this.isDisabledCell(monthIndex) || this.isViewingAll()) {return;}
    const pk = this.pendingKey(itemKey, monthIndex);
    const cleaned = raw.replace(/[^0-9.]/g, '');
    this.pending.update(map => {
      const next = { ...map };
      if (cleaned === '') {
        next[pk] = null;
      } else {
        const dollars = Number(cleaned);
        if (Number.isNaN(dollars)) {return map;}
        next[pk] = Math.round(dollars * 100) * sign;
      }
      return next;
    });
  }

  /** Commit (blur/Enter) — push the pending value to backend if it changed. */
  onCellCommit(itemKey: string, monthIndex: number) {
    if (this.demoBlock.block()) {return;}
    if (this.isDisabledCell(monthIndex) || this.isViewingAll()) {return;}
    const pk = this.pendingKey(itemKey, monthIndex);
    if (!Object.prototype.hasOwnProperty.call(this.pending(), pk)) {return;}
    const next = this.pending()[pk];
    const previous = this.monthValue(itemKey, monthIndex)?.overridden_amount ?? null;
    if (next === previous) {
      // No change versus what BE already has — drop the pending entry.
      this.pending.update(map => {
        const m = { ...map }; delete m[pk]; return m;
      });
      return;
    }
    const addressId = this.selectedAddressId();
    if (addressId === null) {return;}
    this.monthlyFinance.overrideMutation.mutate(
      [{
        address_id: addressId,
        monthly_finance_type_key: itemKey,
        kind: this.layer(),
        month_attribution: this.attribution(),
        year: this.year(),
        month: monthIndex + 1,
        overridden_amount: next,
      }],
      {
        onSuccess: () => {
          // The grid has refetched by now (the mutation waits for it), so it already holds this value.
          // A value typed in the cell meanwhile stays until its own save.
          this.pending.update(map => {
            if (map[pk] !== next) {return map;}
            const m = { ...map }; delete m[pk]; return m;
          });
        },
      },
    );
  }

  // -- Open a cell's transactions --------------------------------------------

  /**
   * A read-only cell opens its transactions on a click. An editable one keeps the
   * click for typing an override, and opens on a double click.
   */
  onCellClick(event: MouseEvent, row: PLRow, monthIndex: number): void {
    if ((event.target as HTMLElement)?.tagName === 'INPUT') { return; }
    this.openCellTransactions(row, monthIndex);
  }

  /** A line item: its own transactions for the month. */
  openCellTransactions(row: PLRow, monthIndex: number): void {
    this.openTransactions([row.key], monthIndex);
  }

  /** A collapsed group or a total adds up several lines, which the list cannot filter to at once. */
  openGroupTransactions(group: PLGroup, monthIndex: number): void {
    this.openTransactions(group.rows.map(r => r.key), monthIndex, group.label);
  }

  /** A total covers whole sections, so it carries every line under them. */
  openTotalTransactions(total: 'income' | 'expenses' | 'noi', monthIndex: number): void {
    const income = this.incomeGroups().flatMap(group => group.rows.map(row => row.key));
    const expenses = this.expenseGroups().flatMap(group => group.rows.map(row => row.key));
    const keys = total === 'income' ? income : total === 'expenses' ? expenses : [...income, ...expenses];
    const label = total === 'income' ? 'Total net income' : total === 'expenses' ? 'Total operating expenses' : 'Net operating income';
    this.openTransactions(keys, monthIndex, label);
  }

  /** Hands the Transactions tab the cell's own filters through the URL. */
  /** `label` names a group or total, so the Transactions page shows it as one filter rather than one per line. */
  private openTransactions(keys: string[], monthIndex: number, label: string | null = null): void {
    if (!this.grid().opensTransactions || this.isDisabledCell(monthIndex)) { return; }
    const month = DateTime.local(this.year(), monthIndex + 1, 1);
    const addressId = this.selectedAddressId();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab: 'transactions',
        category: keys.length ? keys.join(',') : null,
        categoryLabel: keys.length > 1 ? label : null,
        propertyId: addressId ?? null,
        month: month.toFormat('yyyy-MM'),
        [MONTH_ATTRIBUTION_QUERY_PARAM]: monthAttributionQueryValue(this.attribution()),
      },
      queryParamsHandling: 'merge',
    });
  }

  revertCell(itemKey: string, monthIndex: number, event: Event) {
    event.stopPropagation();
    if (this.demoBlock.block()) {return;}
    if (this.isViewingAll()) {return;}
    const pk = this.pendingKey(itemKey, monthIndex);
    // Mark pending = null then commit, which sends overridden_amount: null to BE.
    this.pending.update(map => ({ ...map, [pk]: null }));
    this.onCellCommit(itemKey, monthIndex);
  }

  // -- History dialog --------------------------------------------------------

  async openHistory(): Promise<void> {
    if (this.demoBlock.block()) {return;}
    this.rightSidePanelService.setDialogPageTitle('Override History');
    await this.rightSidePanelService.openRightSidePanel(FinanceHistoryDialogComponent, {
      addressId: this.selectedAddressId(),
      kind: this.layer(),
      monthAttribution: this.attribution(),
      year: this.year(),
    });
  }

  /**
   * Opens the PDF importer. Imports require a single property (the override API
   * is per-address), so it's disabled in the "All Properties" view. Projections
   * land on the 12 months starting next month, so we pre-switch the grid to the
   * Projected layer and the window's start year for immediate feedback.
   */
  async openUploadProjections(): Promise<void> {
    const addressId = this.selectedAddressId();
    if (addressId === null) {
      await this.util.showError('Select a property first to import projections.');
      return;
    }
    const now = new Date();
    const m0 = now.getMonth() + 1; // 0-based index of next month
    const startYear = now.getFullYear() + Math.floor(m0 / 12);
    const startMonth = (m0 % 12) + 1;

    this.layer.set('projected');
    this.year.set(startYear);
    this.pushQueryParams();

    this.rightSidePanelService.setDialogPageTitle('Import Projections');
    await this.rightSidePanelService.openRightSidePanel(UploadProjectionsModalComponent, {
      addressId,
      addressLabel: this.currentPropertyLabel(),
      startMonth,
      startYear,
    });
    this.rightSidePanelService.panelClosed().subscribe(() => this.loadPage(true, false));
  }

  // -- Aggregations ----------------------------------------------------------

  /** Sum of cell values in a group for one month, null if every cell is empty. Signed cents. */
  private groupMonthCents(group: PLGroup, monthIndex: number): number | null {
    if (this.isDisabledCell(monthIndex)) {return null;}
    // Sign comes from the group kind, not the stored value. The backend persists
    // override *magnitudes* (it abs()'s overridden_amount), so expense and
    // contra-revenue cells come back positive. Using abs(cell) * kindSign keeps
    // income / expense / NOI totals correct whether the source (real backend or
    // local mock) stores signed or unsigned amounts.
    const kindSign = group.kind === 'revenue' ? 1 : -1;
    let sum = 0;
    let any = false;
    for (const r of group.rows) {
      const cents = this.effectiveCents(r.key, monthIndex);
      if (cents !== null) { sum += Math.abs(cents) * kindSign; any = true; }
    }
    return any ? sum : null;
  }

  groupMonth(group: PLGroup, monthIndex: number): number | null {
    const c = this.groupMonthCents(group, monthIndex);
    return c === null ? null : Math.abs(c) / 100;
  }

  groupYear(group: PLGroup): number {
    let sum = 0;
    for (let m = 0; m < 12; m++) {sum += this.groupMonthCents(group, m) ?? 0;}
    return Math.abs(sum) / 100;
  }

  rowYear(itemKey: string): number {
    let sum = 0;
    for (let m = 0; m < 12; m++) {
      if (this.isDisabledCell(m)) {continue;}
      sum += this.effectiveCents(itemKey, m) ?? 0;
    }
    return Math.abs(sum) / 100;
  }

  /** Total income for a month (income items are positive, contra-revenue items are stored negative — signed sum already nets). */
  totalIncomeMonth(monthIndex: number): number | null {
    if (this.isDisabledCell(monthIndex)) {return null;}
    let sum = 0;
    let any = false;
    for (const g of this.incomeGroups()) {
      const m = this.groupMonthCents(g, monthIndex);
      if (m !== null) { sum += m; any = true; }
    }
    return any ? sum / 100 : null;
  }

  totalIncomeYear(): number {
    let s = 0;
    for (let m = 0; m < 12; m++) {s += this.totalIncomeMonth(m) ?? 0;}
    return s;
  }

  /** Operating expenses for a month (always non-negative absolute dollars for display). */
  totalExpensesMonth(monthIndex: number): number | null {
    if (this.isDisabledCell(monthIndex)) {return null;}
    let sum = 0;
    let any = false;
    for (const g of this.expenseGroups()) {
      const m = this.groupMonthCents(g, monthIndex);
      if (m !== null) { sum += m; any = true; }
    }
    return any ? Math.abs(sum) / 100 : null;
  }

  totalExpensesYear(): number {
    let s = 0;
    for (let m = 0; m < 12; m++) {s += this.totalExpensesMonth(m) ?? 0;}
    return s;
  }

  /** NOI = income (signed) + expense (signed, negative) → divide-by-100 dollars. */
  noiMonth(monthIndex: number): number | null {
    if (this.isDisabledCell(monthIndex)) {return null;}
    let sum = 0;
    let any = false;
    for (const g of this.incomeGroups()) {
      const m = this.groupMonthCents(g, monthIndex);
      if (m !== null) { sum += m; any = true; }
    }
    for (const g of this.expenseGroups()) {
      const m = this.groupMonthCents(g, monthIndex);
      if (m !== null) { sum += m; any = true; }
    }
    return any ? sum / 100 : null;
  }

  noiYear(): number {
    let s = 0;
    for (let m = 0; m < 12; m++) {s += this.noiMonth(m) ?? 0;}
    return s;
  }

  // -- Manual counter --------------------------------------------------------

  readonly manualCount = computed(() => {
    if (this.isViewingAll()) {return 0;}
    let count = 0;
    for (const cat of this.effectivePL()?.categories ?? []) {
      for (const sub of cat.subcategories) {
        for (const item of sub.items) {
          for (const v of item.values) {
            if (v.overridden_amount !== null) {count++;}
          }
        }
      }
    }
    // Pending entries that set or clear an override also count visually.
    for (const v of Object.values(this.pending())) {
      if (v !== null) {count++;}
    }
    return count;
  });

  // -- Formatters ------------------------------------------------------------

  formatCompact(v: number): string {
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 1_000_000) {return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;}
    if (abs >= 10_000) {return `${sign}$${Math.round(abs / 1000)}k`;}
    if (abs >= 1_000) {return `${sign}$${(abs / 1000).toFixed(1)}k`;}
    return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
  }

  formatOrDash(v: number | null): string {
    return v === null ? '—' : this.formatCompact(v);
  }
}
